import { push, ref, set, update } from "firebase/database";
import { deleteToken, getToken, type Messaging } from "firebase/messaging";
import {
  database,
  firebaseConfig,
  getMessagingIfSupported,
} from "../firebase/firebaseConfig";
import { CoreError, CoreErrorCause } from "../../domain/models/CoreError";
import { CloudMessagingPermission } from "../../domain/models/CloudMessagingPermission";
import { waitForActiveServiceWorker } from "./serviceWorkerActivation";
import {
  XCNotification,
  XCNotificationHelper,
} from "../../domain/notifications/models/Notification";

const SERVICE_WORKER_PATH = "/firebase-messaging-sw.js";

export class PushNotificationsService {
  private static instance: PushNotificationsService;

  private serviceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration> | null =
    null;

  private constructor() {}

  public static getInstance(): PushNotificationsService {
    if (!PushNotificationsService.instance) {
      PushNotificationsService.instance = new PushNotificationsService();
    }
    return PushNotificationsService.instance;
  }

  // ==================== Sending (client-written RTDB queue) ====================

  public async saveNotification(notification: XCNotification): Promise<void> {
    try {
      const firebaseValue = XCNotificationHelper.firebaseValue(notification);
      if (Object.keys(firebaseValue).length === 0) {
        return;
      }

      const notificationRef = push(ref(database, "notifications"));
      if (!notificationRef.key) {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: "Could not generate notification identifier",
        });
      }

      await set(notificationRef, firebaseValue);
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }

      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: "Error saving push notification payload",
        underlyingException: error,
      });
    }
  }

  // ==================== Receiving (FCM web push) ====================

  /**
   * Whether this browser can receive web push at all. False on iOS Safari
   * outside an installed PWA, insecure contexts, and browsers without the
   * Notification/service-worker APIs — for those users email is the channel,
   * and every permission surface is hidden.
   */
  public async isPushSupported(): Promise<boolean> {
    if (
      typeof Notification === "undefined" ||
      !("serviceWorker" in navigator)
    ) {
      return false;
    }
    return (await getMessagingIfSupported()) !== null;
  }

  /**
   * Maps the browser permission onto the shared enum. Browsers have no
   * `provisional` state, so it never occurs on web.
   */
  public async getPermissionStatus(): Promise<CloudMessagingPermission> {
    if (!(await this.isPushSupported())) {
      return CloudMessagingPermission.denied;
    }
    switch (Notification.permission) {
      case "granted":
        return CloudMessagingPermission.authorized;
      case "denied":
        return CloudMessagingPermission.denied;
      default:
        return CloudMessagingPermission.requestable;
    }
  }

  /**
   * Shows the browser permission prompt. Must only be called from a user
   * gesture (button click) — Chrome and Firefox require it, and prompting on
   * load incurs Chrome's quiet-UI penalty.
   */
  public async requestPermissions(): Promise<CloudMessagingPermission> {
    if (!(await this.isPushSupported())) {
      return CloudMessagingPermission.denied;
    }
    try {
      const permission = await Notification.requestPermission();
      switch (permission) {
        case "granted":
          return CloudMessagingPermission.authorized;
        case "denied":
          return CloudMessagingPermission.denied;
        default:
          return CloudMessagingPermission.requestable;
      }
    } catch (error) {
      throw new CoreError({
        cause: CoreErrorCause.PushNotificationsRequestFailure,
        context: "Error requesting notification permission",
        underlyingException: error,
      });
    }
  }

  /**
   * Current FCM registration token, or null while permission is not granted.
   * Web has no `onTokenRefresh` — callers re-run this on every app start and
   * the backend prunes tokens FCM reports dead.
   */
  public async getDeviceToken(): Promise<string | null> {
    return this.obtainDeviceToken(true);
  }

  /**
   * @param reportFailure Whether a failure is reported to Sentry. The retry
   * loop in `registerDeviceToken` only reports its final attempt: a transient
   * failure that the next attempt recovers from is a breadcrumb, not an issue.
   */
  private async obtainDeviceToken(
    reportFailure: boolean,
  ): Promise<string | null> {
    const messaging = await getMessagingIfSupported();
    if (!messaging || Notification.permission !== "granted") {
      return null;
    }

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      // A build-config gap, not a user state — it must reach Sentry.
      new CoreError({
        cause: CoreErrorCause.PushNotificationsRequestFailure,
        context:
          "VITE_FIREBASE_VAPID_KEY is not configured; web push is disabled",
        reportToSentry: reportFailure,
      });
      return null;
    }

    try {
      const registration = await this.getServiceWorkerRegistration();
      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
      return token || null;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw new CoreError({
        cause: CoreErrorCause.PushNotificationsRequestFailure,
        context: "Error obtaining FCM device token",
        underlyingException: error,
        reportToSentry: reportFailure,
      });
    }
  }

  /**
   * Stores the token like RN does, plus the web platform tag the backend uses
   * to tell web tokens from mobile ones (the email migration default).
   */
  public async saveDeviceToken(
    userIdentifier: string,
    deviceToken: string,
  ): Promise<void> {
    try {
      await update(ref(database), {
        [`users/${userIdentifier}/profile/notificationTokens/${deviceToken}`]: true,
        [`users/${userIdentifier}/profile/notificationTokenPlatforms/${deviceToken}`]:
          "web",
      });
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw new CoreError({
        cause: CoreErrorCause.PushNotificationsRequestFailure,
        context: "Error saving device token",
        underlyingException: error,
      });
    }
  }

  /**
   * Sign-out hygiene: removes this browser's token from RTDB (both nodes) and
   * invalidates it with FCM, so user B's browser never receives user A's
   * pushes on a shared machine.
   */
  public async removeDeviceToken(userIdentifier: string): Promise<void> {
    try {
      const messaging = await getMessagingIfSupported();
      if (!messaging) {
        return;
      }
      // getDeviceToken already reported via CoreError; sign-out must not block on it.
      const token = await this.getDeviceToken().catch(() => null);
      if (!token) {
        return;
      }
      await update(ref(database), {
        [`users/${userIdentifier}/profile/notificationTokens/${token}`]: null,
        [`users/${userIdentifier}/profile/notificationTokenPlatforms/${token}`]:
          null,
      });
      await this.deleteLocalToken(messaging);
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw new CoreError({
        cause: CoreErrorCause.PushNotificationsRequestFailure,
        context: "Error removing device token",
        underlyingException: error,
      });
    }
  }

  /** Resolves `true` once a token was saved, `false` if every attempt failed. */
  public async registerDeviceToken(
    userIdentifier: string,
    retries: number = 3,
    delayMs: number = 1000,
  ): Promise<boolean> {
    for (let i = 0; i < retries; i++) {
      const isLastAttempt = i === retries - 1;
      try {
        const token = await this.obtainDeviceToken(isLastAttempt);
        if (token) {
          await this.saveDeviceToken(userIdentifier, token);
          return true;
        }
      } catch {
        // Each failing attempt already logged via its CoreError; only the
        // last one is reported to Sentry.
      }

      if (i < retries - 1) {
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), delayMs);
        });
      }
    }
    return false;
  }

  /**
   * One static SW file for every environment: the Firebase config rides in on
   * the registration URL's query params.
   */
  private getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
    if (!this.serviceWorkerRegistrationPromise) {
      const configEntries = {
        apiKey: firebaseConfig.apiKey,
        projectId: firebaseConfig.projectId,
        messagingSenderId: firebaseConfig.messagingSenderId,
        appId: firebaseConfig.appId,
      };
      const missingKeys = Object.entries(configEntries)
        .filter(([, value]) => !value)
        .map(([key]) => key);
      if (missingKeys.length > 0) {
        // A worker registered with empty credentials fails silently at push
        // time — refuse instead, so the misconfigured build reaches Sentry.
        throw new CoreError({
          cause: CoreErrorCause.PushNotificationsRequestFailure,
          context: `Firebase config incomplete (${missingKeys.join(", ")}); refusing to register messaging service worker`,
        });
      }
      const params = new URLSearchParams({
        apiKey: configEntries.apiKey ?? "",
        projectId: configEntries.projectId ?? "",
        messagingSenderId: configEntries.messagingSenderId ?? "",
        appId: configEntries.appId ?? "",
      });
      this.serviceWorkerRegistrationPromise = navigator.serviceWorker
        .register(`${SERVICE_WORKER_PATH}?${params.toString()}`)
        .then(async (registration) => {
          // On a first visit the worker is still installing when register()
          // resolves; FCM's PushManager.subscribe needs it active.
          await waitForActiveServiceWorker(registration);
          return registration;
        })
        .catch((error) => {
          // Allow a later attempt instead of caching the failure forever.
          this.serviceWorkerRegistrationPromise = null;
          throw error;
        });
    }
    return this.serviceWorkerRegistrationPromise;
  }

  private async deleteLocalToken(messaging: Messaging): Promise<void> {
    try {
      await deleteToken(messaging);
    } catch {
      // The RTDB removal already happened; a failed local invalidation only
      // means the next getToken returns the same token again.
    }
  }
}
